import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword, signOut,
  sendPasswordResetEmail, createUserWithEmailAndPassword,
  sendEmailVerification, updateProfile, deleteUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { camposCodigoUsado } from '../services/registro';
import { detect as detectIdioma } from '../i18n';

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);
export const ROL = { MASTER: 'master', EVAL: 'eval', CIUDAD: 'ciudad' };

function detectRol(d) {
  if (d?.isMaster) return ROL.MASTER;
  const r = d?.roles?.cities;
  if (r === 'master' || r === 'admin') return ROL.MASTER;
  if (r === 'eval' || r === 'evaluador') return ROL.EVAL;
  // P0-REG: 'prov'/'city'/'organizacion' son valores legado escritos por builds antiguas
  if (r === 'ciudad' || r === 'provincia' || r === 'org' ||
      r === 'prov' || r === 'city' || r === 'organizacion') return ROL.CIUDAD;
  // Legacy fields
  if (d?.role === 'admin' || d?.role === 'master') return ROL.MASTER;
  if (d?.role === 'evaluador' || d?.role === 'eval') return ROL.EVAL;
  if (d?.role === 'ciudad') return ROL.CIUDAD;
  return null;
}

// P0-EVAL: idioma del perfil con paridad iOS/Android. iOS/Android escriben prefs.idioma y
// iOS usa 'pt' (no 'pt-BR'). Se normaliza a los 7 códigos de la web.
const IDIOMAS_OK = ['es','en','fr','de','it','nl','pt-BR'];
function idiomaPerfil(d) {
  let v = d?.prefs?.idioma || d?.idioma || '';
  if (!v) return null;
  if (v === 'pt' || v.toLowerCase() === 'pt-br') v = 'pt-BR';
  return IDIOMAS_OK.includes(v) ? v : null;
}

/** P0-EVAL: guarda el idioma elegido en el perfil (ambos campos) para que no se revierta. */
export async function guardarIdiomaPerfil(uid, code) {
  if (!uid || !IDIOMAS_OK.includes(code)) return;
  try {
    await setDoc(doc(db, 'users', uid),
      { idioma: code, prefs: { idioma: code }, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) { console.warn('[idioma] no se pudo guardar en el perfil:', e?.code || e); }
}

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [usuario, setUsu]   = useState(null);
  const [rol, setRol]       = useState(null);
  const [estado, setEstado] = useState('cargando');
  // P0-REG: mientras se registra, /registro NO redirige (si el batch falla y se hace
  // rollback, el usuario debe seguir viendo el formulario con el mensaje de error).
  const [registrando, setRegistrando] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, async fu => {
      if (!fu) { setUser(null); setUsu(null); setRol(null); setEstado('noauth'); return; }
      setUser(fu);
      const leerDoc = () => getDoc(doc(db, 'users', fu.uid));
      try {
        let snap;
        try {
          snap = await leerDoc();                         // 1er intento
        } catch (e1) {
          // C-20: 1er intento falló (posible parpadeo de red). Esperamos y reintentamos UNA vez.
          console.warn('[AUTH] getDoc users falló (intento 1), reintento en 900ms:', e1?.code || e1?.message || e1);
          await new Promise(r => setTimeout(r, 900));
          snap = await leerDoc();                          // reintento (si vuelve a lanzar -> catch externo)
        }
        console.log('[AUTH] uid:', fu.uid, 'exists:', snap.exists());
        if (snap.exists()) {
          const d = { id: fu.uid, ...snap.data() };
          console.log('[AUTH] isMaster:', d.isMaster, 'roles:', d.roles, 'detectRol:', detectRol(d));
          setUsu(d); setRol(detectRol(d));
          // Sincronizar idioma del perfil Firestore con localStorage Y avisar a LangProvider.
          // Lote 1: antes solo se guardaba en localStorage (sin evento); el snapshot posterior
          // ya lo veía igual y no aplicaba nada → la web seguía en el idioma previo al login.
          const idi = idiomaPerfil(d);
          if (idi && idi !== localStorage.getItem('acescn_lang')) {
            localStorage.setItem('acescn_lang', idi);
            window.dispatchEvent(new StorageEvent('storage', { key: 'acescn_lang', newValue: idi }));
          }
        }
      } catch (e) {
        // Ambos intentos fallaron: NO colgar en 'cargando'. Estado recuperable ("sin rol").
        console.error('[AUTH] Error leyendo users/' + fu.uid + ' tras reintento:', e?.code || e?.message || e);
      } finally {
        setEstado('auth');   // red final: SIEMPRE resuelve, nunca Splash infinito
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) {
        const d={id:user.uid,...snap.data()}; setUsu(d); setRol(detectRol(d));
        const idi = idiomaPerfil(d);
        // P0-EVAL: solo re-aplicar si CAMBIÓ respecto al actual (antes se re-aplicaba en cada
        // snapshot del doc users y deshacía el idioma recién elegido en la cabecera).
        if (idi && idi !== localStorage.getItem('acescn_lang')) {
          localStorage.setItem('acescn_lang', idi);
          // Disparar evento para que LangProvider actualice
          window.dispatchEvent(new StorageEvent('storage', {key:'acescn_lang', newValue:idi}));
        }
      }
    });
  }, [user]);

  const login = (e,p) => signInWithEmailAndPassword(auth,e,p);
  const logout = () => signOut(auth);
  const resetPassword = e => sendPasswordResetEmail(auth,e);

  // P0-REG (23 sep 2026): registro ATÓMICO.
  //  - users/{uid} + marca del código van en UN batch (antes: dos escrituras sueltas;
  //    si la 2ª fallaba, la cuenta quedaba creada y el código sin consumir).
  //  - Si el batch falla, se BORRA la cuenta de Auth recién creada (rollback). Así el
  //    usuario puede reintentar con el mismo email en vez de chocar con
  //    "email ya registrado" y quedarse con una cuenta sin rol.
  //  - isMaster siempre false: las reglas prohíben auto-asignarse master/admin.
  //  - Idioma en ambos campos (idioma + prefs.idioma) para paridad iOS/Android.
  //  - La vinculación ciudad→candidatura la hace la Cloud Function onCodigoCiudadCanjeado.
  const registrar = async ({ email, password, nombre, apellidos, pais, paisISO, provincia,
                              ciudad, cargo, telefono, talla, fechaNacimiento, rolCN, whatsapp,
                              codigo, candidaturaId }) => {
    setRegistrando(true);
    try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;
    // Mismo idioma que ve la pantalla (guardado → navegador → inglés). Antes caía a 'es' y
    // dejaba perfiles de países no hispanohablantes en español.
    const idioma = detectIdioma();
    try {
      const batch = writeBatch(db);
      batch.set(doc(db, 'users', uid), {
        id:uid, email, nombre, apellidos, pais:pais||'', paisISO:paisISO||'', provincia:provincia||'',
        ciudad:ciudad||'', cargo:cargo||null, telefono:telefono||'',
        talla:talla||null, fechaNacimiento:fechaNacimiento||null, whatsapp:whatsapp||null,
        activo:true, isMaster:false, roles:{cities:rolCN},
        emailVerificado:true,
        accessCodeUsed: codigo || null,
        candidaturasCN: candidaturaId ? [candidaturaId] : [],
        evaluacionesCN:[],
        idioma, prefs:{ idioma },
        createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
      }, { merge: true });
      if (codigo) {
        batch.set(doc(db, 'cn_codigos', codigo), camposCodigoUsado(uid), { merge: true });
      }
      await batch.commit();
    } catch (e) {
      console.error('[registro] Batch users+codigo falló, deshaciendo cuenta Auth:', e?.code, e?.message);
      try { await deleteUser(cred.user); } catch (e2) {
        console.error('[registro] No se pudo borrar la cuenta Auth huérfana:', e2?.code, e2?.message);
        try { await signOut(auth); } catch {}
      }
      const err = new Error('registro_error_general');
      err.code = 'cn/registro-rollback';
      err.cause = e;
      throw err;
    }
    try { await updateProfile(cred.user, { displayName: `${nombre} ${apellidos}` }); } catch {}
    return cred.user;
    } finally {
      setRegistrando(false);
    }
  };

  const reenviarVerificacion = () => user ? sendEmailVerification(user) : Promise.reject('no auth');
  const refrescarVerificado = async () => { if(!user) return false; await user.reload(); return auth.currentUser?.emailVerified||false; };

  return (
    <Ctx.Provider value={{
      user, usuario, rol, estado, registrando, uid: user?.uid,
      isMaster: rol===ROL.MASTER, isEval: rol===ROL.EVAL, isCiudad: rol===ROL.CIUDAD,
      emailVerified: user?.emailVerified||false,
      login, logout, resetPassword, registrar, reenviarVerificacion, refrescarVerificado,
    }}>
      {children}
    </Ctx.Provider>
  );
}
